import type { ReactNode } from 'react';

import { ArticleItemBody } from '#components/Article/Items/ArticleItemBody';
import { ArticleItemImage } from '#components/Article/Items/ArticleItemImage';
import { ArticleItemModuleTypes } from '#components/Article/Items/ArticleItemModuleTypes';
import { ArticleItemTeaserText } from '#components/Article/Items/ArticleItemTeaserText';
import { ArticleItemTitleLink } from '#components/Article/Items/ArticleItemTitleLink';
import { PublicationDate } from '#components/Article/PublicationDate';
import { BoardModuleMedia } from '#components/Board/Modules/BoardModuleMedia';
import { useCommentsCount } from '#components/CommentsCount/use-comments-count';
import { ImageModule } from '#components/Image/ImageModule';
import { Link } from '#components/Link/Link';
import { LiveStreamOverlay } from '#components/Media/LiveStreamOverlay';
import { BreakpointMedia } from '#cutils/breakpoints/media';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { COMMENT_BUTTON_ID } from '#cutils/hooks/useComments';
import type { ArticleTeaser } from '#domains/resolver/board-section-data';
import { TrackEventDataType } from '#types/tracking';
import { Tracktor } from 'react-tracktor';

import styles from './BoardSectionItemArticle.module.scss';
import { BoardSectionItemAuthor } from './BoardSectionItemAuthor';

type Props = {
	teaser: ArticleTeaser;
	isLarge?: boolean;
	isPriority?: boolean;
	className?: string;
	sizes: string;
};

export function BoardSectionItemArticle({ teaser, isLarge, isPriority, className = '', sizes }: Props) {
	const {
		constants: { appUrl },
		flags: { IS_COMMENTS_HIDDEN },
	} = useEnvironment();

	const commentCount = useCommentsCount(teaser.rowId);
	const showComments = teaser.allowComments && !IS_COMMENTS_HIDDEN && commentCount !== undefined && commentCount !== 0;

	const modulesTypes = teaser.modulesTypes;

	const authorComponent = (
		<>
			<address>
				<BoardSectionItemAuthor author={teaser.author} />
			</address>

			{teaser.author && showComments ? <span className={styles.dot}>&bull;</span> : null}
			{showComments ? (
				<>
					<Link href={{ pathname: teaser.link, hash: COMMENT_BUTTON_ID }}>
						{commentCount} {commentCount == 1 ? 'Kommentar' : 'Kommentare'}
					</Link>
				</>
			) : null}
		</>
	);

	let mediasection: Exclude<ReactNode, null | undefined>;

	// during mapping of board data the information if media should playable or only the teaser image should be shown is evaluated
	if (teaser.media.playable) {
		mediasection = (
			<BoardModuleMedia
				teaserModule={teaser.media}
				articlePath={teaser.link}
				isPriority={isPriority}
				publicationDate={teaser.publicationDate}
				sizes={sizes}
			/>
		);
	} else if (teaser.media.type === 'livestream') {
		// if it is a livestream and not playable show the live badge on every teaser image
		mediasection = (
			<LiveStreamOverlay
				image={{
					...teaser.media.thumbnail,
					link: teaser.link,
					sizes,
					isPriority,
				}}
				livestream={{
					rowId: teaser.media.rowId,
					hls: teaser.media.url,
					start: teaser.media.startDate,
					end: teaser.media.endDate,
					hasRecordings: teaser.media.onDemandVideos.length > 0,
				}}
			/>
		);
	} else {
		mediasection = (
			<ArticleItemImage
				image={<ImageModule image={teaser.media} isPriority={isPriority} enableZoomEffect={true} sizes={sizes} />}
				path={teaser.link}
				title={teaser.title}
			/>
		);
	}

	return (
		<Tracktor
			trackingData={{
				data: {
					target: {
						title: teaser.title,
						url: `${appUrl}${teaser.link}`,
					},
					type: TrackEventDataType.BoardClick,
				},
			}}
		>
			{({ trackEvent }) => (
				<>
					<article className={`${styles.wrapper} ${isLarge ? styles.isLargeWrapper : ''} ${className}`} onClick={trackEvent}>
						{mediasection}

						<div className={styles.content}>
							<header className={styles.header}>
								<section className={styles.metaModules}>
									<PublicationDate className="body6" publicationDate={teaser.publicationDate} />

									<ArticleItemModuleTypes modulesTypes={modulesTypes} />
								</section>

								<ArticleItemTitleLink className={styles.title} headingClassName="heading1" path={teaser.link}>
									{teaser.title}
								</ArticleItemTitleLink>
							</header>

							<BreakpointMedia greaterThanOrEqual="md">
								{(className, renderChildren) => {
									return (
										<ArticleItemBody className={`${className} ${styles.teaser}`}>
											{renderChildren ? <ArticleItemTeaserText>{teaser.teaserText ?? ''}</ArticleItemTeaserText> : null}
										</ArticleItemBody>
									);
								}}
							</BreakpointMedia>

							<footer className={styles.footer}>
								<section className={`body6 underline-hover ${styles.authorsComments}`}>{authorComponent}</section>

								{teaser.relatedLink ? (
									<>
										{/* This is the line that is displayed between the article and the links. */}
										<hr className={styles.line} />
										<section>
											<Link className="body3 underline-hover" href={teaser.relatedLink.href} data-testid="mzt-link">
												<span className={styles.relatedLink}>
													<span className="body5">Mehr zum Thema:</span> {teaser.relatedLink.text}
												</span>
											</Link>
										</section>
									</>
								) : null}
							</footer>
						</div>
					</article>
				</>
			)}
		</Tracktor>
	);
}
